import { z } from "zod";
import { EProductSubType, EProductType } from "../../constants/product.constants";
import { paginationMetaSchema } from "../base-api-response";

export enum EProductStatusV4 {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  DRAFT = "DRAFT",
  ARCHIVED = "ARCHIVED",
}

export const productOptionResponseSchema = z.object({
  id: z.string(),
  productId: z.string(),
  name: z.string(),
  position: z.number(),
  values: z.array(z.string()),
});

export const productImageResponseSchema = z.object({
  id: z.union([z.string(), z.number()]),
  alt: z.string().optional(),
  position: z.number(),
  productId: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  width: z.number(),
  height: z.number(),
  src: z.string(),
  variantIds: z.array(z.string()),
});

export const variantImageResponseSchema = z.object({
  url: z.string(),
  alt: z.string().nullish(),
});

export const productVariantPartnerEarningResponseSchema = z.object({
  basePay: z.number(),
});

export const productVariantResponseSchema = z.object({
  id: z.string(),
  productId: z.string().optional(),
  shopifyId: z.string().nullish(),
  title: z.string(),
  price: z.number(),
  position: z.number(),
  compareAtPrice: z.number().optional(),
  code: z.string().optional(),
  status: z.string(),
  duration: z.number().nullish(),
  image: variantImageResponseSchema.nullish(),
  images: z.array(productImageResponseSchema).optional(),
  earning: productVariantPartnerEarningResponseSchema.nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
  imageId: z.number().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const variantOptionValueResponseSchema = z.object({
  name: z.string(),
  code: z.string(),
  description: z.string().nullish(),
  badge: z.string().nullish(),
  badgeColor: z.string().nullish(),
  noOfWalks: z.number().int().nullish(),
});

export const variantOptionResponseSchema = z.object({
  name: z.string(),
  key: z.string(),
  values: z.array(variantOptionValueResponseSchema),
  priorityOrder: z.number(),
});

export const productDetailsResponseSchema = z.object({
  id: z.string(),
  shopifyId: z.string(),
  title: z.string(),
  type: z.nativeEnum(EProductType),
  subType: z.nativeEnum(EProductSubType),
  createdAt: z.number(),
  updatedAt: z.number(),
  tags: z.array(z.string()).optional(),
  status: z.nativeEnum(EProductStatusV4),
  cities: z.array(z.string()).nullish().optional(),
  // Not emitted by the products DTO on every endpoint; consumers already treat it as absent-able.
  variantOptions: z.array(variantOptionResponseSchema).optional(),
  variants: z.array(productVariantResponseSchema),
  images: z.array(productImageResponseSchema),
  meta: z.record(z.string(), z.unknown()).nullish(),
  rating: z.number().optional(),
});

/** Product embedded on order line items — single `variant`, `options`, and `images`. */
export const productSingleVariantResponseSchema = z
  .object({
    id: z.string(),
    shopifyId: z.string().nullish(),
    title: z.string(),
    type: z.nativeEnum(EProductType),
    subType: z.nativeEnum(EProductSubType),
    tags: z.array(z.string()).optional(),
    status: z.nativeEnum(EProductStatusV4),
    code: z.string().optional(),
    price: z.number().optional(),
    compareAtPrice: z.number().optional(),
    variant: productVariantResponseSchema,
    options: z.array(productOptionResponseSchema),
    images: z.array(productImageResponseSchema),
    createdAt: z.number(),
    updatedAt: z.number(),
    // `.nullish()`, not `.optional()`: the product `meta` column is nullable and the backend mapper
    // emits `meta: null` for products without meta.
    meta: z.record(z.string(), z.unknown()).nullish(),
    rating: z.number().optional(),
    cities: z.array(z.string()).nullish().optional(),
  })
  .passthrough();

export const productsListResponseSchema = z.object({
  items: z.array(productDetailsResponseSchema),
  meta: paginationMetaSchema,
});

export const searchProductsRequestSchema = z.object({
  types: z.array(z.nativeEnum(EProductType)).nullish(),
  subTypes: z.array(z.nativeEnum(EProductSubType)).nullish(),
  cities: z.array(z.string().trim().min(1)).nullish(),
  status: z.nativeEnum(EProductStatusV4).optional(),
  search: z.string().trim().min(1).optional(),
  page: z.preprocess((val) => (val ? Number(val) : 1), z.number().int().min(1).default(1)),
  limit: z.preprocess((val) => (val ? Number(val) : 20), z.number().int().min(1).max(100).default(20)),
});

const createProductVariantImageSchema = z.object({
  url: z.string(),
  alt: z.string().nullish().optional(),
});

export const createProductVariantSchema = z.object({
  shopifyId: z.string().min(1),
  title: z.string().min(1),
  price: z.number().nonnegative(),
  position: z.number().int().positive(),
  sku: z.string().nullish().optional(),
  code: z.string().nullish().optional(),
  compareAtPrice: z.number().nonnegative().nullish().optional(),
  status: z.string().min(1),
  duration: z.number().int().positive().nullish().optional(),
  image: createProductVariantImageSchema.nullish().optional(),
  customAttributes: z.record(z.string(), z.unknown()).optional(),
});

const createProductPlanSchema = z.object({
  code: z.string().min(1),
  isActive: z.boolean().nullish(),
  priority: z.number().int().nullish(),
});

const createProductVariantOptionValueSchema = z.object({
  name: z.string().min(1),
  code: z.string().min(1),
  description: z.string().nullish(),
  badge: z.string().nullish(),
  badgeColor: z.string().nullish(),
  noOfWalks: z.number().int().nullish(),
});

const createProductVariantOptionSchema = z.object({
  name: z.string().min(1),
  key: z.string().min(1),
  priorityOrder: z.number().int(),
  values: z.array(createProductVariantOptionValueSchema),
});

const createProductOriginalOptionSchema = z.object({
  shopifyId: z.string().min(1),
  productShopifyId: z.string().min(1),
  name: z.string().min(1),
  position: z.number().int(),
  values: z.array(z.string()),
});

/** GET response / stored product image (full record). */
export const createProductImageSchema = z.object({
  id: z.number(),
  alt: z.string().nullish(),
  position: z.number().int(),
  productId: z.string().min(1),
  createdAt: z.number(),
  updatedAt: z.number(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  src: z.string().min(1),
  variantIds: z.array(z.string()),
});

/** POST / PATCH body: admin uploads supply `src` (and optional `position`, `alt`). */
export const createProductImageInputSchema = z.object({
  src: z.string().min(1),
  position: z.number().int().positive(),
  alt: z.string().nullish().optional(),
});

export const createProductRequestSchema = z.object({
  shopifyId: z.string().min(1),
  title: z.string().min(1),
  type: z.nativeEnum(EProductType),
  subType: z.nativeEnum(EProductSubType),
  status: z.nativeEnum(EProductStatusV4).default(EProductStatusV4.DRAFT),
  cities: z.array(z.string()).nullish().optional(),
  tags: z.array(z.string()).nullish().optional(),
  vendor: z.string().nullish().optional(),
  handle: z.string().nullish().optional(),
  bodyHtml: z.string().nullish().optional(),
  price: z.number().nonnegative().nullish().optional(),
  compareAtPrice: z.number().nonnegative().nullish().optional(),
  meta: z.record(z.string(), z.unknown()).nullish().optional(),
  rating: z.number().nonnegative().nullish().optional(),
  plan: createProductPlanSchema.nullish().optional(),
  variantOptions: z.array(createProductVariantOptionSchema).nullish().optional(),
  originalOptions: z.array(createProductOriginalOptionSchema).nullish().optional(),
  images: z.array(createProductImageInputSchema).nullish().optional(),
  variants: z.array(createProductVariantSchema).min(1),
});

// ─── PATCH /products/:id (partial body; no product-level shopifyId) ───────────

const updateProductVariantImageSchema = z.object({
  url: z.string().min(1),
  alt: z.string().nullish(),
});

const updateProductPlanSchema = z.object({
  code: z.string().min(1),
  isActive: z.boolean().nullish(),
  priority: z.number().int().nullish(),
});

const updateProductVariantOptionValueSchema = z.object({
  name: z.string().min(1),
  code: z.string().min(1),
  description: z.string().nullish(),
  badge: z.string().nullish(),
  badgeColor: z.string().nullish(),
  noOfWalks: z.number().int().nullish(),
});

const updateProductVariantOptionSchema = z.object({
  name: z.string().min(1),
  key: z.string().min(1),
  priorityOrder: z.number().int(),
  values: z.array(updateProductVariantOptionValueSchema),
});

const updateProductOriginalOptionSchema = z.object({
  shopifyId: z.string().min(1),
  productShopifyId: z.string().min(1),
  name: z.string().min(1),
  position: z.number().int(),
  values: z.array(z.string()),
});

export const updateProductImageInputSchema = z.object({
  id: z.number().optional(),
  src: z.string().min(1),
  position: z.number().int().positive(),
  alt: z.string().nullish().optional(),
});

export const updateProductVariantRequestSchema = z.object({
  shopifyId: z.string().min(1, "Variant shopifyId is required"),
  title: z.string().min(1).optional(),
  price: z.number().nonnegative().optional(),
  position: z.number().int().positive().optional(),
  sku: z.string().nullish(),
  code: z.string().nullish(),
  compareAtPrice: z.number().nonnegative().nullish(),
  status: z.string().optional(),
  duration: z.number().int().positive().nullish(),
  image: updateProductVariantImageSchema.optional(),
  customAttributes: z.record(z.string(), z.unknown()).optional(),
});

export const updateProductRequestSchema = z.object({
  title: z.string().min(1).optional(),
  type: z.nativeEnum(EProductType).optional(),
  subType: z.nativeEnum(EProductSubType).optional(),
  status: z.nativeEnum(EProductStatusV4).optional(),
  cities: z.array(z.string()).nullish(),
  tags: z.array(z.string()).nullish(),
  vendor: z.string().nullish(),
  handle: z.string().nullish(),
  bodyHtml: z.string().nullish(),
  price: z.number().nonnegative().nullish(),
  compareAtPrice: z.number().nonnegative().nullish(),
  meta: z.record(z.string(), z.unknown()).nullish(),
  rating: z.number().nonnegative().nullish(),
  plan: updateProductPlanSchema.nullish(),
  variantOptions: z.array(updateProductVariantOptionSchema).nullish(),
  originalOptions: z.array(updateProductOriginalOptionSchema).nullish(),
  images: z.array(updateProductImageInputSchema).nullish(),
  variants: z.array(updateProductVariantRequestSchema).optional(),
});

export type ProductsListResponse = z.infer<typeof productsListResponseSchema>;
export type ProductOptionResponse = z.infer<typeof productOptionResponseSchema>;
export type ProductImageResponse = z.infer<typeof productImageResponseSchema>;
export type ProductVariantResponse = z.infer<typeof productVariantResponseSchema>;
export type ProductSingleVariantResponse = z.infer<typeof productSingleVariantResponseSchema>;
export type ProductDetailsResponse = z.infer<typeof productDetailsResponseSchema>;
export type VariantOptionValueResponse = z.infer<typeof variantOptionValueResponseSchema>;
export type VariantOptionResponse = z.infer<typeof variantOptionResponseSchema>;
export type SearchProductsRequest = z.infer<typeof searchProductsRequestSchema>;
export type CreateProductRequest = z.infer<typeof createProductRequestSchema>;
export type UpdateProductVariantRequestDto = z.infer<typeof updateProductVariantRequestSchema>;
export type UpdateProductRequestDto = z.infer<typeof updateProductRequestSchema>;